"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { CircleHelp } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import enMessages from "@/messages/en.json";
import CompareShareUrlBox from "@/components/compare/CompareShareUrlBox";

const DEFAULT_HERO_HELP_ARIA = enMessages.ComparePage.heroShareHelpAria;

function HelpPopover({
  ariaLabel,
  children,
}: {
  ariaLabel: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        className={cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-full border border-transparent text-gray-500 transition-colors hover:border-gray-200 hover:bg-gray-50 hover:text-gray-800 dark:hover:border-gray-700 dark:hover:bg-gray-900 dark:hover:text-gray-200",
          open &&
            "border-gray-200 bg-gray-50 text-[#E63946] dark:border-gray-700 dark:bg-gray-900 dark:text-[#ff6b73]",
        )}
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <CircleHelp size={20} strokeWidth={2} aria-hidden />
      </button>
      {open ? (
        <div
          role="tooltip"
          className="absolute right-0 top-full z-[70] mt-2 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white p-3 text-xs leading-relaxed text-gray-700 shadow-lg dark:border-gray-600 dark:bg-gray-950 dark:text-gray-200"
        >
          <div className="space-y-2 text-pretty">{children}</div>
        </div>
      ) : null}
    </div>
  );
}

export default function ComparePageHero({
  shareUrl,
  listingsCount,
  /** Передаёт страница через getTranslations; fallback — ключ из en, чтобы не было MISSING_MESSAGE */
  heroHelpAriaLabel = DEFAULT_HERO_HELP_ARIA,
}: {
  shareUrl: string;
  listingsCount: number;
  heroHelpAriaLabel?: string;
}) {
  const t = useTranslations("ComparePage");

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
      <h1 className="text-balance text-2xl font-bold text-gray-900 dark:text-gray-100 md:text-3xl">{t("heroTitle")}</h1>
      <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-2 sm:gap-x-3">
        <CompareShareUrlBox variant="full" compact shareUrl={shareUrl} listingsCount={listingsCount} />
        <HelpPopover ariaLabel={heroHelpAriaLabel}>
          <p className="m-0">{t("heroSubtitle")}</p>
          <p className="m-0 pt-2 text-gray-600 dark:text-gray-400">{t("shareTooltipFullLead", { count: listingsCount })}</p>
        </HelpPopover>
      </div>
    </div>
  );
}
